// ===== Packages =====

import {
    Node,
    Editor,
    Transforms,
    Range,
    Path,
    NodeEntry,
}                           from 'slate';
import { v4 as uuidv4 }     from 'uuid';

// ===== Elements =====

import LineElement          from '../elements/lineElement';
import _HeaderElement       from '../elements/headerElement';

// ===== Interfaces =====

import {
    EditorNode,
    HeaderNode,
}                           from '../interfaces';

// ===== Enums =====

import { HEADER_SIZE }      from '../elements/enums';

export const HeaderElement = _HeaderElement;

const headerSizeIsValid = (headerSize: HEADER_SIZE | undefined): boolean => !!headerSize
    && Object.values(HeaderElement.HEADER_SIZE).includes(headerSize);

const getSelectionHeaders = (editor: Editor, headerSize = HeaderElement.HEADER_SIZE.one): NodeEntry<Node>[] => {
    if (headerSizeIsValid(headerSize)) {
        const isHeaderOfSize = (node: EditorNode | Node, size: number): boolean => HeaderElement.isHeaderElement(node)
            && 'headerSize' in node
            && node.headerSize === size;
        return Array.from(Editor.nodes(editor, { match: (node) => isHeaderOfSize(node, headerSize) }));
    }
    return [];
};

const hasSelectedH1 = (editor: Editor): boolean => {
    const [match] = getSelectionHeaders(editor);
    return !!match;
};

const hasSelectedH2 = (editor: Editor): boolean => {
    const [match] = getSelectionHeaders(editor, HeaderElement.HEADER_SIZE.two);
    return !!match;
};

/**
 * Returns true when the current selection is entirely inside Header 2 nodes
 * whose reader Part marker has been explicitly hidden.
 */
const hasSelectedH2PartMarkerHidden = (editor: Editor): boolean => {
    const selectionHeaders = getSelectionHeaders(editor, HeaderElement.HEADER_SIZE.two);
    if (selectionHeaders.length === 0) {
        return false;
    }

    return selectionHeaders.every(([node]) => HeaderElement.hidesPartMarker(node));
};

const getExistingParagraphId = (node: HeaderNode): string => {
    const { paragraphId } = node;
    return typeof paragraphId === 'string' ? paragraphId.trim() : '';
};

const setAsHeaderElement = (editor: Editor, headerSize = HeaderElement.HEADER_SIZE.one): void => {
    const newHeaderSize = headerSizeIsValid(headerSize) ? headerSize : HeaderElement.HEADER_SIZE.one;
    Editor.withoutNormalizing(editor, () => {
        Array.from(Editor.nodes(editor, { match: HeaderElement.implementsLineInterface })).forEach(([node, path]) => {
            const existingParagraphId = getExistingParagraphId(node as HeaderNode);
            Transforms.setNodes(
                editor,
                HeaderElement.newHeaderElement({
                    headerSize: newHeaderSize,
                    paragraphId: existingParagraphId || uuidv4(),
                }),
                { at: path },
            );
        });

        // NOTE: some LineInterface elements implement the ListableInterfaceElement
        //  so we make sure that all Headers set here no longer implement the ListableInterface
        Transforms.unsetNodes(editor, 'isListable', { match: HeaderElement.isHeaderElement });
    });
};

const setAsH1 = (editor: Editor): void => setAsHeaderElement(editor, HeaderElement.HEADER_SIZE.one);

const setAsH2 = (editor: Editor): void => setAsHeaderElement(editor, HeaderElement.HEADER_SIZE.two);

/**
 * Toggles whether the selected Header 2 nodes opt out of the reader Part
 * marker treatment.
 */
const toggleH2PartMarker = (editor: Editor): void => {
    const selectedHeaderTwos = getSelectionHeaders(editor, HeaderElement.HEADER_SIZE.two);
    if (selectedHeaderTwos.length === 0) {
        return;
    }

    const shouldHidePartMarker = !selectedHeaderTwos.every(([node]) => (
        HeaderElement.hidesPartMarker(node)
    ));

    Editor.withoutNormalizing(editor, () => {
        selectedHeaderTwos.forEach(([, path]) => {
            if (shouldHidePartMarker) {
                Transforms.setNodes<HeaderNode>(editor, { hidePartMarker: true }, { at: path });
                return;
            }

            Transforms.unsetNodes(editor, 'hidePartMarker', { at: path });
        });
    });
};

const toggleH1 = (editor: Editor): void => {
    if (hasSelectedH1(editor)) {
        const matchH1 = (node: EditorNode | Node): boolean => HeaderElement.isHeaderElement(node)
            && 'headerSize' in node
            && node.headerSize === HeaderElement.HEADER_SIZE.one;
        Editor.withoutNormalizing(editor, () => {
            Array.from(Editor.nodes(editor, { match: matchH1 })).forEach(([node, path]) => {
                const existingParagraphId = getExistingParagraphId(node as HeaderNode);
                Transforms.setNodes(editor, {
                    ...LineElement.newLineElement(),
                    ...(existingParagraphId ? { paragraphId: existingParagraphId } : {}),
                }, { at: path });
                // NOTE: no non-Header LineInterface elements should have headerSize
                //  so we remove it here
                Transforms.unsetNodes(editor, 'headerSize', { at: path });
            });
        });
    } else {
        setAsH1(editor);
    }
};

const toggleH2 = (editor: Editor): void => {
    if (hasSelectedH2(editor)) {
        const matchH2 = (node: EditorNode | Node): boolean => HeaderElement.isHeaderElement(node)
            && 'headerSize' in node
            && node.headerSize === HeaderElement.HEADER_SIZE.two;
        Editor.withoutNormalizing(editor, () => {
            Array.from(Editor.nodes(editor, { match: matchH2 })).forEach(([node, path]) => {
                const existingParagraphId = getExistingParagraphId(node as HeaderNode);
                Transforms.setNodes(editor, {
                    ...LineElement.newLineElement(),
                    ...(existingParagraphId ? { paragraphId: existingParagraphId } : {}),
                }, { at: path });
                // NOTE: no non-Header LineInterface elements should have headerSize
                //  so we remove it here
                Transforms.unsetNodes(editor, 'headerSize', { at: path });
            });
        });
    } else {
        setAsH2(editor);
    }
};

export const HeaderHelpers = {
    getSelectionHeaders,
    headerSizeIsValid,
    setAsHeaderElement,
    hasSelectedH1,
    hasSelectedH2,
    hasSelectedH2PartMarkerHidden,
    toggleH1,
    toggleH2,
    toggleH2PartMarker,
};

export const withHeaders = (editor: Editor, _readOnly?: boolean): Editor => {
    const {
        normalizeNode,
        isVoid,
        isInline,
        insertBreak,
    } = editor;

    // eslint-disable-next-line no-param-reassign
    editor.normalizeNode = (entry) => {
        const [_, path] = entry;
        // NOTE: manually grab the node because entry will be changed
        //  to path in the near future per https://github.com/ianstormtaylor/slate/issues/3275
        const node = Node.get(editor, path) as HeaderNode;

        if (!HeaderElement.isHeaderElement(node)) { return normalizeNode(entry); }

        const header = node;

        if (Editor.hasBlocks(editor, header)) { return Transforms.unwrapNodes(editor, { at: path }); }

        const headerDeclaresIsLineProperty = header.isLine === true;
        if (!headerDeclaresIsLineProperty) { return Transforms.setNodes<HeaderNode>(editor, { isLine: true }, { at: path }); }

        const paragraphId = typeof header.paragraphId === 'string'
            ? header.paragraphId.trim()
            : '';
        if (!paragraphId) {
            return Transforms.setNodes<HeaderNode>(editor, { paragraphId: uuidv4() }, { at: path });
        }

        if (!headerSizeIsValid(header.headerSize)) {
            return Transforms.setNodes<HeaderNode>(editor, { headerSize: HeaderElement.HEADER_SIZE.one }, { at: path });
        }

        if (
            header.headerSize !== HeaderElement.HEADER_SIZE.two
            && 'hidePartMarker' in header
        ) {
            return Transforms.unsetNodes(editor, 'hidePartMarker', { at: path });
        }

        const headerIsFirstChildOfEditor = Path.equals(path, [0]);
        if (headerIsFirstChildOfEditor) {
            if (node.isFirstElement !== true) {
                return Transforms.setNodes<HeaderNode>(editor, { isFirstElement: true }, { at: path });
            }
        } else if ('isFirstElement' in node) {
            return Transforms.unsetNodes(editor, 'isFirstElement', { at: path });
        }
    };

    // eslint-disable-next-line no-param-reassign
    editor.insertBreak = () => {
        const [headerEntry] = Array.from(Editor.nodes(editor, { match: HeaderElement.isHeaderElement }));
        if (headerEntry) {
            const { selection } = editor;
            const [start, end] = Range.edges(selection as Range);
            const [startHeaderEntry] = Array.from(Editor.nodes(editor, { at: start.path, match: HeaderElement.isHeaderElement }));
            const [endHeaderEntry] = Array.from(Editor.nodes(editor, { at: end.path, match: HeaderElement.isHeaderElement }));
            if (startHeaderEntry && endHeaderEntry) {
                insertBreak();
                return Editor.withoutNormalizing(editor, () => {
                    Transforms.setNodes(editor, LineElement.newLineElement());
                    Transforms.unsetNodes(editor, 'headerSize');
                });
            }
            if (startHeaderEntry) {
                const [[endNode, endPath]] = Array.from(Editor.nodes(editor, { at: end.path, match: (n) => Editor.isBlock(editor, n), mode: 'lowest' }));
                insertBreak();
                return Editor.withoutNormalizing(editor, () => {
                    Transforms.setNodes(editor, endNode as EditorNode);
                    Transforms.unsetNodes(editor, ['headerSize', 'isLine']);
                });
            }
            if (endHeaderEntry) {
                insertBreak();
                const { headerSize } = endHeaderEntry[0] as HeaderNode;
                return Transforms.setNodes(editor, HeaderElement.newHeaderElement({ headerSize }));
            }
        }

        return insertBreak();
    };

    // eslint-disable-next-line no-param-reassign
    editor.isInline = (element) => (HeaderElement.isHeaderElement(element) ? false : isInline(element));

    // eslint-disable-next-line no-param-reassign
    editor.isVoid = (node) => (HeaderElement.isHeaderElement(node) ? false : isVoid(node));

    return editor;
};
